<template>
  <div class="base-container">
    <el-container class="feed-formats-page">
      <el-main class="shadow">
        <Sticky :z-index="10" :sticky-top="50" class-name="filter-panel">
          <FilterPanel
            v-model="filter"
            :reset-value="defaultFilter"
            @update="update"
          />
        </Sticky>
      </el-main>
    </el-container>
    <el-main class="new-feed-format-block">
      <router-link to="/integrations/new">
        <el-button
          class="mr-1"
          type="primary"
          size="small"
          icon="PlusElementIcon"
          round
        >
          Add integration format
        </el-button>
      </router-link>
    </el-main>
    <LoadingBlock :loading="loading" />
    <div v-if="!loading && items.length > 0">
      <IntegrationsCard
        v-for="item in items"
        :key="item.id"
        :feed-format-id="item.id"
      />
      <Pagination
        v-if="total > 0"
        class="mt-1"
        :total="total"
        :page="page"
        @pagination="update"
      />
    </div>
    <div
      v-if="!loading && items.length < 1"
      class="el-alert el-alert--warning is-light empty-result"
    >
      <h4>
        We haven't found any matches for your request, you can
        <el-link type="primary" @click.prevent="reset"> clear filters </el-link>
      </h4>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, reactive } from 'vue'
import { useFeedFormatsStore } from '@/apps/traforama/store/feedFormats'
import IntegrationsCard from './components/IntegrationsCard.vue'
import FilterPanel from './components/FilterPanel.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import Pagination from '@/components/Pagination.vue'
import Sticky from '@/components/Sticky.vue'

import { IFeedFormatsPayload } from '@/api/feed_formats'

export default defineComponent({
  name: 'FeedFormatsList',
  components: {
    IntegrationsCard,
    LoadingBlock,
    Pagination,
    FilterPanel,
    Sticky
  },
  setup(_props, context) {
    const feedFormatsStore = useFeedFormatsStore()

    const total = computed(() => feedFormatsStore.total)
    const items = computed(() => feedFormatsStore.currentItems)
    const page = computed(() => feedFormatsStore.currentPage)
    const loading = computed(() => feedFormatsStore.listLoading)
    const filter = computed({
      get: () => feedFormatsStore.filter,
      set: (val: IAdScanAlertFilter) => feedFormatsStore.SetFilters(val)
    })
    const defaultFilter = computed(() => feedFormatsStore.defaultFilter)

    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      feedFormatsStore.SetCurrentPage(newPage)
      feedFormatsStore.FetchItems()
    }

    const reset = () => {
      filter.value = JSON.parse(JSON.stringify(defaultFilter.value))
      update({})
    }

    onMounted(() => update({}))

    const notFiltered = computed(() => {
      return Object.values(filter.value).length < 1
    })

    onMounted(() => {
      update({})
    })

    return {
      items,
      loading,
      total,
      page,
      update,
      filter,
      defaultFilter,
      notFiltered,
      reset
    }
  }
})
</script>

<style lang="scss" scoped>
.feed-formats-page {
  display: flex;
  justify-content: space-between;
}

.el-container {
  margin: 0;
}

:deep(.filter-panel) {
  border-radius: 5px;

  .el-card__body {
    padding: 0px 5px;
  }

  h3 {
    margin: 2px 8px 5px 8px;
    padding-top: 5px;
  }
}

.new-feed-format-block {
  display: flex;
  justify-content: flex-end;
  padding: 8px 20px;
}

.empty-result {
  margin: 0 20px;
}

:deep(.el-loading-mask) {
  margin: 0px 20px;
}

:deep(.el-card.is-always-shadow) {
  box-shadow: none;
}
</style>
